import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ReportingService } from 'src/app/shared/store/reporting/reporting.service';
import {
  MarketingPerformanceReportItem,
  UNASSIGNED_FACET_ID,
} from 'src/app/shared/store/reporting/types';
import { TimeZoneService } from '../../../../common/time-zone/time-zone.service';
import { toNaiveISO } from '../../../../shared/helper/datehelper';
import { Observable, Subject } from 'rxjs';
import { ViewService } from '../../../../shared/store/view/views.service';
import { View } from '../../../../shared/store/view/types';
import { debounceTime, filter, take } from 'rxjs/operators';
import { BreadcrumbService } from 'xng-breadcrumb';
import { NavigationStart, Router } from '@angular/router';
import { NavigationMemoryService } from '../../../../shared/store/navigation-memory/navigation-memory.service';

/** Structured custom-dropdown selection persisted across a drill-down round-trip. */
interface MarketingComparisonFilters {
  period: string;
  salesTeamIds: number[];
  salesPersonIds: number[];
  campaignIds: number[];
  sourceIds: number[];
  mediumIds: number[];
}

@UntilDestroy()
@Component({
  selector: 'app-sales-comparison-marketing-dashboard',
  templateUrl: './sales-comparison-marketing-dashboard.component.html',
  styleUrls: ['./sales-comparison-marketing-dashboard.component.scss'],
  standalone: false,
})
export class SalesComparisonMarketingDashboardComponent implements OnInit {
  // --- subheader state ---
  startDate!: Date;
  endDate!: Date;
  period: string = 'CurrentMonth';
  salesTeamIds: number[] = [-1];
  salesPersonIds: number[] = [-1];
  campaignIds: number[] = [-1];
  mediumIds: number[] = [-1];
  sourceIds: number[] = [-1];

  /** Identifies this dashboard's slot in the shared navigation-memory store. */
  private readonly memoryKey = 'salesComparisonMarketing';

  readonly salesTeamIdAllValue = -1;
  readonly salesPersonIdAllValue = -1;
  readonly campaignIdAllValue = -1;
  readonly mediumIdAllValue = -1;
  readonly sourceIdAllValue = -1;

  marketingPerformanceData$: Observable<MarketingPerformanceReportItem[]>;
  view$: Observable<View>;

  private readonly fetchTrigger$ = new Subject<void>();

  constructor(
    private readonly viewService: ViewService,
    private readonly reportingService: ReportingService,
    private readonly timeZoneService: TimeZoneService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly router: Router,
    private readonly navigationMemoryService: NavigationMemoryService,
  ) {
    this.view$ = this.viewService.getViews.value$;
    this.marketingPerformanceData$ =
      this.reportingService.getMarketingPerformance.value$;
  }

  ngOnInit(): void {
    // Restore the custom-dropdown selection if we came straight back from a
    // drill-down (synchronous read: the store emits its current value on
    // subscribe, before the child subheader initialises its inputs).
    this.navigationMemoryService
      .getMemory()
      .pipe(take(1))
      .subscribe((memory) => {
        if (
          this.navigationMemoryService.isValid(
            memory,
            this.memoryKey,
            this.router.url,
          )
        ) {
          const payload = memory.payload as MarketingComparisonFilters;
          this.period = payload.period;
          this.salesTeamIds = payload.salesTeamIds;
          this.salesPersonIds = payload.salesPersonIds;
          this.campaignIds = payload.campaignIds;
          this.sourceIds = payload.sourceIds;
          this.mediumIds = payload.mediumIds;
        }
        this.navigationMemoryService.reset();
      });

    // Arm the memory only on a real drill-down (the smart-action-link sets the
    // `useTempFilter` navigation state); the target URL is whatever it routes to.
    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        untilDestroyed(this),
      )
      .subscribe((e) => {
        const state = this.router.getCurrentNavigation()?.extras.state;
        if (state && state['useTempFilter'] === true) {
          this.navigationMemoryService.keep<MarketingComparisonFilters>(
            this.memoryKey,
            {
              period: this.period,
              salesTeamIds: this.salesTeamIds,
              salesPersonIds: this.salesPersonIds,
              campaignIds: this.campaignIds,
              sourceIds: this.sourceIds,
              mediumIds: this.mediumIds,
            },
            this.router.url,
            e.url,
          );
        }
      });

    this.view$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@salesDashboard', x.title);
      });

    this.fetchTrigger$
      .pipe(debounceTime(0), untilDestroyed(this))
      .subscribe(() => {
        this.reportingService.getMarketingPerformance.call({
          filter: this.buildFilter(),
        });
      });
  }

  // Subheader 2-way bindings trigger these when the user changes a filter.
  // startDate/endDate arrive first via the period preset; we wait for both before firing.
  onStartDateChange(startDate: Date) {
    this.startDate = startDate;
    this.triggerFetch();
  }

  onEndDateChange(endDate: Date) {
    this.endDate = endDate;
    this.triggerFetch();
  }

  onSalesTeamIdsChange(ids: number[]) {
    this.salesTeamIds = ids;
    this.triggerFetch();
  }

  onSalesPersonIdsChange(ids: number[]) {
    this.salesPersonIds = ids;
    this.triggerFetch();
  }

  onCampaignIdsChange(ids: number[]) {
    this.campaignIds = ids;
    this.triggerFetch();
  }

  onSourceIdsChange(ids: number[]) {
    this.sourceIds = ids;
    this.triggerFetch();
  }

  onMediumIdsChange(ids: number[]) {
    this.mediumIds = ids;
    this.triggerFetch();
  }

  private triggerFetch() {
    if (!this.startDate || !this.endDate) return;
    this.fetchTrigger$.next();
  }

  /**
   * Composes the Gridify filter string sent to the (future) marketing endpoint.
   */
  private buildFilter(): string {
    const clauses: string[] = [];

    const endDatePlusOne: Date = new Date(this.endDate);
    endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);

    clauses.push(
      `creationDate>=${this.timeZoneService.fromLocalToUtc(
        toNaiveISO(this.startDate),
        false,
      )}`,
    );
    clauses.push(
      `CreationDate<${this.timeZoneService.fromLocalToUtc(
        toNaiveISO(endDatePlusOne),
        false,
      )}`,
    );

    // User-facing filters
    // TODO(étape 2): champs Gridify SalesTeamId/SalesPersonId à confirmer côté backend.
    this.appendClause(
      clauses,
      'SalesTeamId',
      this.salesTeamIds,
      this.salesTeamIdAllValue,
    );
    this.appendClause(
      clauses,
      'SalesPersonId',
      this.salesPersonIds,
      this.salesPersonIdAllValue,
    );
    this.appendClause(
      clauses,
      'CampaignId',
      this.campaignIds,
      this.campaignIdAllValue,
    );
    this.appendClause(
      clauses,
      'SourceId',
      this.sourceIds,
      this.sourceIdAllValue,
    );
    this.appendClause(
      clauses,
      'MediumId',
      this.mediumIds,
      this.mediumIdAllValue,
    );

    return clauses.join(',');
  }

  private appendClause(
    clauses: string[],
    field: string,
    ids: number[],
    allValue: number,
  ): void {
    if (!ids || ids.length === 0 || ids.includes(allValue)) return;

    // The "unassigned" bucket is the UI sentinel -2 internally; it travels to
    // the API as null (Gridify IS NULL). -2 never leaves the front.
    clauses.push(
      `(${ids
        .map((id) =>
          id === UNASSIGNED_FACET_ID ? `${field}=null` : `${field}=${id}`,
        )
        .join('|')})`,
    );
  }
}
